import { useEffect, useRef, useState } from "react";
import { Box, Button, Flex, Text, useToast } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import {
  buildCheckIpCurl,
  dryCheckOwnIp,
  DryCheckResponse,
} from "api/ApiKeySettings";
import CheckIpErrorMessage from "components/Checkers/IpCheckerBlock/CheckIpErrorMessage";
import { copyToClipboard } from "utils/manipulation/maskApiKey";
import { FOCUS_RING } from "utils/chackra/controlStyles";
import Panel from "./Panel";

const codeBlock = {
  as: "pre",
  fontFamily: "mono",
  fontSize: "sm",
  color: "text.primary",
  bg: "bg.page",
  borderWidth: "1px",
  borderColor: "border.subtle",
  borderRadius: "md",
  p: 3,
  overflowX: "auto",
  whiteSpace: "pre",
  // A scrollable region must be reachable by keyboard.
  tabIndex: 0,
  _focusVisible: FOCUS_RING,
} as const;

const CURL_MASKED_HINT_ID = "api-test-curl-masked";

interface ApiTestRequestPanelProps {
  apiKey: string;
  /** The key as the page shows it: the command line never reveals more. */
  shownKey: string;
  requestsLeft: number | null;
}

/**
 * One free check of the customer's own IP with their key, through the public
 * `/check-ip` with `dry=true`: the raw response their code receives, and the
 * command line for the same call (spec 2026-10-20-website-api-settings, US5).
 */
const ApiTestRequestPanel = ({ apiKey, shownKey, requestsLeft }: ApiTestRequestPanelProps) => {
  const intl = useIntl();
  const toast = useToast();
  const t = (id: string, values?: Record<string, any>) =>
    intl.formatMessage({ id }, values);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DryCheckResponse | null>(null);
  const [error, setError] = useState<unknown>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const curl = buildCheckIpCurl(shownKey);
  // A command with the masked key can't run: copying it is offered only once
  // the key is shown, and the hint says how to get there.
  const keyMasked = shownKey !== apiKey;

  const run = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await dryCheckOwnIp(apiKey);
      if (mounted.current) setResult(data);
    } catch (err) {
      if (mounted.current) setError(err);
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  const copyCurl = async () => {
    const ok = await copyToClipboard(curl);
    toast({
      position: "top",
      status: ok ? "success" : "error",
      title: t(ok ? "profileApi.test.curlCopied" : "profileApi.key.copyFailed"),
      duration: 2000,
      isClosable: true,
    });
  };

  return (
    <Panel id="api-test" title={t("profileApi.test.heading")}>
      <Text fontSize="sm" color="text.secondary" mb={4}>
        {t("profileApi.test.hint")}
      </Text>
      <Button
        colorScheme="blue"
        onClick={run}
        isLoading={loading}
        loadingText={t("profileApi.test.running")}
        _focusVisible={FOCUS_RING}
      >
        {t("profileApi.test.run")}
      </Button>

      <Box role="status" aria-live="polite" mt={4}>
        {result && (
          <Text fontSize="sm" color="text.primary" data-testid="api-test-charge">
            {t("profileApi.test.free")}{" "}
            {t(
              result.would_charge
                ? "profileApi.test.wouldCharge"
                : "profileApi.test.wouldNotCharge"
            )}
            {requestsLeft !== null &&
              ` ${t("profileApi.test.requestsLeft", { count: requestsLeft })}`}
          </Text>
        )}
      </Box>

      {error ? <CheckIpErrorMessage error={error} /> : null}

      {result && (
        <Box mt={3}>
          <Text fontSize="sm" color="text.secondary" mb={1} id="api-test-response-label">
            {t("profileApi.test.response")}
          </Text>
          <Box
            {...codeBlock}
            maxH="24rem"
            overflowY="auto"
            aria-labelledby="api-test-response-label"
            data-testid="api-test-response"
          >
            {JSON.stringify(result, null, 2)}
          </Box>
        </Box>
      )}

      <Box mt={5}>
        <Text fontSize="sm" color="text.secondary" mb={1} id="api-test-curl-label">
          {t("profileApi.test.curl")}
        </Text>
        <Box {...codeBlock} aria-labelledby="api-test-curl-label" data-testid="api-test-curl">
          {curl}
        </Box>
        <Flex mt={2} gap={3} align="center" wrap="wrap">
          <Button
            variant="outline"
            color="brand.solid"
            borderColor="border.strong"
            _hover={{ bg: "bg.surfaceHover" }}
            _focusVisible={FOCUS_RING}
            size="sm"
            onClick={copyCurl}
            isDisabled={keyMasked}
            aria-describedby={keyMasked ? CURL_MASKED_HINT_ID : undefined}
          >
            {t("profileApi.test.copyCurl")}
          </Button>
          {keyMasked && (
            <Text id={CURL_MASKED_HINT_ID} fontSize="sm" color="text.secondary">
              {t("profileApi.test.curlMasked")}
            </Text>
          )}
        </Flex>
      </Box>
    </Panel>
  );
};

export default ApiTestRequestPanel;
